/**
 * Report viewer — CLIENT track (Phase 1 · Step 6 · Phase-2 Slice 3).
 *
 * Where a client reads their DELIVERED reports. Slice 3 makes this REAL:
 *  - resolves the signed-in client's OWN projects (live, client-scoped) and
 *    lets them pick when they have more than one;
 *  - streams the active project's DELIVERED reports
 *    (clients/{cid}/reports where project_id == pid, status delivered);
 *  - on open, mints a SHORT-lived signed URL via the getReportDownloadUrl CF
 *    (the server is the SOLE entitlement authority) and renders the PDF
 *    read-only via pdfjs (ReportPdfViewer — salvaged from the legacy viewer).
 *
 * A client sees ONLY their own projects' delivered reports — never a draft /
 * approved (in-review) report, never another client's report. No money
 * internals / review layer here by construction (owner-locked spine).
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { visibilityFor } from '../lib/roles';
import { Box, Btn3, EmptyNote } from '../components/ui';
import { useToast } from '../components/Toast';
import { ListLoading } from '../components/ListState';
import ReportPdfViewer from '../components/ReportPdfViewer';
import { listenClientProjects } from '../lib/clientProjects';
import type { DemoProject } from '../lib/trackDemo';
import {
  listenProjectReports,
  getReportDownloadUrl,
  reportStatusOf,
  downloadTierFromAttachment,
  type ReportDoc,
} from '../lib/reportDelivery';

export default function Report() {
  const { role, clientId } = useAuth();
  visibilityFor(role); // client floor — no money/review here by construction
  const { toast } = useToast();

  // ── This client's own projects (live, client-scoped). ──
  const [projects, setProjects] = useState<DemoProject[]>([]);
  const [projLoading, setProjLoading] = useState(true);
  const [activePid, setActivePid] = useState<string | null>(null);

  useEffect(() => {
    const unsub = listenClientProjects(
      clientId,
      (state) => {
        setProjects(state.projects);
        // Default to the first project until the client picks another.
        setActivePid((cur) => cur ?? state.projects[0]?.pid ?? null);
      },
      {
        onErr: () => toast('Could not load your projects (check connection)'),
        onReady: () => setProjLoading(false),
      },
    );
    return unsub;
  }, [clientId, toast]);

  const activeProject = useMemo(
    () => projects.find((p) => p.pid === activePid) ?? null,
    [projects, activePid],
  );

  // ── Delivered reports for the active project. ──
  const [reports, setReports] = useState<ReportDoc[]>([]);
  const [repLoading, setRepLoading] = useState(true);

  useEffect(() => {
    if (!clientId || !activePid) {
      setReports([]);
      setRepLoading(false);
      return;
    }
    setRepLoading(true);
    const unsub = listenProjectReports(
      clientId,
      activePid,
      (docs) => {
        // Defence-in-depth: the query is already delivered-only (a client may
        // not read any other status — rules + the deliveredOnly constraint).
        setReports(docs.filter((r) => reportStatusOf(r) === 'delivered'));
        setRepLoading(false);
      },
      () => {
        toast('Could not load your reports (check connection)');
        setRepLoading(false);
      },
      true, // deliveredOnly — REQUIRED for a client (rules + query constraint)
    );
    return unsub;
  }, [clientId, activePid, toast]);

  if (projLoading) {
    return (
      <Box title="Your report">
        <ListLoading label="Loading your engagement…" rows={2} />
      </Box>
    );
  }

  if (!clientId || projects.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Box title="Your report">
          <EmptyNote title="No report delivered yet">
            Your report will appear here the moment it is delivered. You'll
            also see it as an entry in your conversation.
          </EmptyNote>
        </Box>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {projects.length > 1 && (
        <Box title="Choose a project" note="You have more than one engagement">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {projects.map((p) => (
              <button
                key={p.pid}
                type="button"
                className={`btn3 ${p.pid === activePid ? 'ink' : 'gh'}`}
                onClick={() => setActivePid(p.pid)}
              >
                {p.vp}
              </button>
            ))}
          </div>
        </Box>
      )}

      <Box
        title="Your report"
        note={activeProject ? `${activeProject.vp} · ${activeProject.tier}` : 'Your engagement'}
      >
        {repLoading ? (
          <ListLoading label="Loading your reports…" rows={1} />
        ) : reports.length === 0 ? (
          <EmptyNote title="No report delivered yet">
            Your report will appear here the moment it is delivered. You'll
            also see it as an entry in your conversation.
          </EmptyNote>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {reports.map((r) => (
              <DeliveredReport
                key={r.id}
                report={r}
                clientId={clientId}
                projectId={activePid as string}
              />
            ))}
          </div>
        )}
      </Box>
    </div>
  );
}

/**
 * One delivered report. The PDF is NOT loaded until the client clicks Open —
 * a signed URL is short-lived, so we mint it on demand and render read-only.
 */
function DeliveredReport({
  report,
  clientId,
  projectId,
}: {
  report: ReportDoc;
  clientId: string;
  projectId: string;
}) {
  const { toast } = useToast();
  const [url, setUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [opening, setOpening] = useState(false);

  const tier = downloadTierFromAttachment(report.tier);
  const label = `${report.tier ?? 'Report'} · v${report.version_n ?? 1}`;

  async function open() {
    if (opening) return;
    if (!tier) {
      toast('This report type is not yet available to open in-app.');
      return;
    }
    setOpening(true);
    try {
      const res = await getReportDownloadUrl({
        clientId,
        projectId,
        tier,
        runId: report.run_id,
        versionN: report.version_n,
      });
      setUrl(res.url);
      setFileName(res.fileName);
    } catch (e) {
      // The server is the sole authority — surface its message verbatim.
      toast((e as Error).message || 'Could not open the report.');
    } finally {
      setOpening(false);
    }
  }

  return (
    <div
      style={{
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-sm)',
        padding: 14,
      }}
    >
      {!url ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span aria-hidden style={{ fontSize: 20 }}>▤</span>
          <b style={{ fontSize: 14 }}>{label}</b>
          <span style={{ marginLeft: 'auto' }}>
            <Btn3 intent="au" onClick={open} disabled={opening}>
              {opening ? 'Opening…' : '⟡ Open delivered report'}
            </Btn3>
          </span>
        </div>
      ) : (
        <ReportPdfViewer url={url} fileName={fileName} />
      )}
    </div>
  );
}
